import type { ClassProvider, OnDestroy, OnInit, Type } from '@angular/core';
import {
  ChangeDetectorRef,
  Directive,
  EventEmitter,
  InjectionToken,
  Injector,
} from '@angular/core';

import type { Observable } from 'rxjs';
import { NEVER, ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export type WysiwygEmbeddableComponentType = Type<WysiwygEmbeddableComponent<any, any>>;

export const WysiwygEmbeddableComponents = new InjectionToken<WysiwygEmbeddableComponentType[]>(
  'WysiwygEmbeddableComponents',
);

export const WysiwygEmbeddedComponentInitialData = new InjectionToken<any>(
  'WysiwygEmbeddedComponentInitialData',
);

export class WysiwygAction<T extends string, P> {
  constructor(
    public readonly type: T,
    public readonly payload: P,
    public readonly trackingProps?: { contextId: string; hostId: string },
  ) {}
  public withTrackingProps(contextId: string, hostId: string): WysiwygAction<T, P> {
    return new WysiwygAction(this.type, this.payload, { contextId, hostId });
  }
}

@Directive({ standalone: true })
export abstract class WysiwygEmbeddableComponent<S, O extends WysiwygAction<any, any>>
  implements OnInit, OnDestroy
{
  public input$: Subject<S> = new ReplaySubject(1);
  public output$ = new EventEmitter<O>();

  public state$: Observable<S> = this.input$.asObservable();
  public state: S = this.injector.get(WysiwygEmbeddedComponentInitialData, {});

  protected cdRef: ChangeDetectorRef = this.injector.get(ChangeDetectorRef);
  protected ngOnDestroy$ = new Subject<void>();

  constructor(protected injector: Injector) {}

  public ngOnInit(): void {
    this.state$
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(state => this.onStateChange(state, this.state));
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public emit(action: O): void {
    this.output$.emit(action);
  }

  protected onStateChange(nextState: S, prevState: S): void {
    this.state = nextState;
    this.cdRef.detectChanges();
  }
}

// TODO: types
export abstract class WysiwygData {
  public abstract stream(): Observable<any>;
}

export class NoopWysiwygData extends WysiwygData {
  public stream(): Observable<void> {
    return NEVER;
  }
}

export interface WysiwygDataProvider extends ClassProvider {
  provide: typeof WysiwygData;
}
